import { router } from 'expo-router';
import { capitalize } from 'lodash';
import { useEffect } from 'react';
import { SubmitHandler, useForm, UseFormTrigger } from 'react-hook-form';
import { StyleSheet, Text, View } from 'react-native';

import { useAuth } from '@/contexts/authContext';
import { PastorData, pastorResolver } from '@/lib/validations/NpoOnboardingSchema';
import { i18n } from '@/utils/i18n';

import AddAnotherButton from '../buttons/AddAnotherButton';
import BrandedButton from '../buttons/BrandedButton';
import TextInputField from '../inputs/TextInputField';

interface PastorFormProps {
  initialData?: PastorData;
  showDoneButton: boolean;
  showBackButton?: boolean;
  onAdd?: (data: PastorData) => void;
  onDone?: (data: PastorData, trigger: UseFormTrigger<PastorData>) => void;
  isLoading?: boolean;
  manualValidation?: boolean;
  doneLabel?: string;
}

const PastorForm = ({
  initialData,
  showDoneButton,
  showBackButton = false,
  onDone,
  isLoading = false,
  manualValidation = false,
  onAdd,
  doneLabel = i18n.t('mix.done')
}: PastorFormProps) => {
  const { state } = useAuth();

  const type = state.signUpData?.loginEmail?.type;

  const isIndividual = state.authReady && state.user?.user.role === 'INDIVIDUAL';

  const {
    control,
    getValues,
    setFocus,
    reset,
    trigger,
    formState: { isValid, submitCount, errors }
  } = useForm<PastorData>({
    defaultValues: initialData,
    mode: 'onChange',
    resolver: pastorResolver as any,
    context: { isIndividual }
  });

  const onSubmit: SubmitHandler<PastorData> = async (data) => {
    if (onDone) {
      onDone(data, trigger);
    }
  };

  useEffect(() => {
    if (initialData) {
      reset(initialData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  return (
    <View style={styles.formContainer}>
      <View style={styles.inputsContainer}>
        <TextInputField
          name="position"
          control={control}
          label={i18n.t('sign_in.nonProfit.pastors.position_label')}
          infoSubLabel={i18n.t('sign_in.nonProfit.pastors.position_sublabel')}
          placeholder={i18n.t('sign_in.nonProfit.pastors.position_placeholder')}
          returnKeyLabel="next"
          returnKeyType="next"
          onSubmitEditing={() => setFocus('fullName')}
        />

        <TextInputField
          name="fullName"
          control={control}
          label={i18n.t('sign_in.nonProfit.pastors.fullName_label')}
          infoSubLabel={i18n.t('sign_in.nonProfit.pastors.fullName_sublabel')}
          placeholder={i18n.t('sign_in.nonProfit.pastors.fullName_placeholder')}
          returnKeyLabel="next"
          returnKeyType="next"
          onSubmitEditing={() => setFocus('annualSalary')}
        />

        <TextInputField
          name="annualSalary"
          control={control}
          label={i18n.t('sign_in.nonProfit.pastors.annualSalary_label')}
          infoSubLabel={i18n.t('sign_in.nonProfit.pastors.annualSalary_sublabel')}
          placeholder={i18n.t('sign_in.nonProfit.pastors.annualSalary_placeholder')}
          type="numeric"
          currency
          returnKeyType="done"
          onSubmitEditing={() => setFocus('allowance')}
        />

        {type === 'CHURCH' && (
          <TextInputField
            name="allowance"
            control={control}
            label={i18n.t('sign_in.nonProfit.pastors.allowance_label')}
            placeholder={i18n.t('sign_in.nonProfit.pastors.allowance_placeholder')}
            type="numeric"
            currency
            returnKeyType="done"
          />
        )}

        {onAdd && (
          <AddAnotherButton
            onPress={() => {
              trigger().then((valid) => {
                if (valid) onAdd(getValues());
              });
            }}
            label={i18n.t('sign_in.nonProfit.pastors.submit_add_another')}
          />
        )}
      </View>
      {errors.root !== null && <Text style={styles.errorText}>{errors?.root?.message}</Text>}

      {showDoneButton && (
        <View style={styles.nextBackContainer}>
          {showBackButton && router.canGoBack() && (
            <BrandedButton style={styles.flexOne} disabled={isLoading} onPress={router.back}>
              {capitalize(i18n.t('mix.back'))}
            </BrandedButton>
          )}
          <BrandedButton
            style={styles.flexOne}
            onPress={() => {
              if (manualValidation) {
                onSubmit(getValues());
              } else {
                trigger().then((valid) => {
                  if (valid) onSubmit(getValues());
                });
              }
            }}
            disabled={(!isValid && submitCount > 0) || isLoading}
            loading={isLoading}>
            {doneLabel}
          </BrandedButton>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  formContainer: {
    flex: 1,
    justifyContent: 'space-between'
  },

  inputsContainer: {
    gap: 15
  },

  flexOne: {
    flex: 1
  },

  nextBackContainer: {
    flexDirection: 'row',
    width: '100%',
    gap: 10
  },

  errorText: {
    color: 'red',
    fontSize: 20,
    fontWeight: 'bold'
  }
});

export default PastorForm;
